import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  AvailabilitySetAck,
  ProfessionalOnlineStatus,
} from '@klaro7/shared-types';
import { toast } from 'sonner';

import { useSharedSocket } from '@/features/calls/context/CallContext';
import { useMyOnlineStatusState } from './useMyOnlineStatus';
import {
  MY_PROFESSIONAL_PROFILE_QUERY_KEY,
  patchCachedProfessional,
} from '../lib/profileCache';

type SelectableStatus = Exclude<ProfessionalOnlineStatus, 'busy'>;

/**
 * The server refuses the online toggle when profile requirements are missing.
 * Show them verbatim — a generic "controlla i requisiti" left the consultant
 * with a toggle that flipped back for no stated reason.
 */
function describeAvailabilityRejection(result: AvailabilitySetAck): string {
  const missing = result?.missingRequirements ?? [];
  if (missing.length > 0) {
    const list = missing.join(' · ');
    return result?.message ? `${result.message} ${list}` : list;
  }
  return result?.message ?? 'Riprova tra qualche secondo.';
}

export function useProfessionalPresence() {
  const onlineStatusState = useMyOnlineStatusState();
  const confirmedStatus = onlineStatusState.status;
  const socket = useSharedSocket();
  const queryClient = useQueryClient();
  const [requestedStatus, setRequestedStatus] = useState<SelectableStatus | null>(null);
  const [isConnected, setIsConnected] = useState(socket?.connected ?? false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeRequestRef = useRef(0);

  const clearPending = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    setRequestedStatus(null);
  }, []);

  useEffect(() => () => {
    activeRequestRef.current += 1;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  }, []);

  useEffect(() => {
    if (!socket) {
      setIsConnected(false);
      return;
    }
    const handleConnect = () => setIsConnected(true);
    const handleDisconnect = () => setIsConnected(false);
    setIsConnected(socket.connected);
    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [socket]);

  useEffect(() => {
    if (
      requestedStatus &&
      (confirmedStatus === 'busy' || confirmedStatus === requestedStatus)
    ) {
      // A live server transition wins over the optimistic selection. Invalidating
      // the request also prevents its later acknowledgement from reverting it.
      activeRequestRef.current += 1;
      clearPending();
    }
  }, [clearPending, confirmedStatus, requestedStatus]);

  const setStatus = useCallback((next: SelectableStatus) => {
    if (confirmedStatus === 'busy') {
      toast.info('Disponibilità bloccata durante la sessione');
      return;
    }
    if (onlineStatusState.isLoading || onlineStatusState.isError) return;
    if (!socket?.connected) {
      toast.error('Connessione non disponibile', {
        description: 'Riprova tra qualche secondo.',
      });
      return;
    }

    const requestId = activeRequestRef.current + 1;
    activeRequestRef.current = requestId;
    setRequestedStatus(next);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (activeRequestRef.current !== requestId) return;
      activeRequestRef.current += 1;
      timeoutRef.current = null;
      setRequestedStatus(null);
      void queryClient.invalidateQueries({ queryKey: MY_PROFESSIONAL_PROFILE_QUERY_KEY });
      toast.error('Disponibilità non aggiornata', {
        description: 'Il server non ha risposto. Riprova.',
      });
    }, 5000);

    socket.emit(
      'availability:set',
      { status: next },
      (result: AvailabilitySetAck) => {
        if (activeRequestRef.current !== requestId) return;
        if (!result?.ok) {
          queryClient.setQueryData(
            MY_PROFESSIONAL_PROFILE_QUERY_KEY,
            (old: unknown) => patchCachedProfessional(old, {
              onlineStatus: result.onlineStatus,
            })
          );
          clearPending();
          void queryClient.invalidateQueries({
            queryKey: MY_PROFESSIONAL_PROFILE_QUERY_KEY,
          });
          toast.error('Disponibilità non aggiornata', {
            description: describeAvailabilityRejection(result),
          });
          return;
        }

        clearPending();
        queryClient.setQueryData(
          MY_PROFESSIONAL_PROFILE_QUERY_KEY,
          (old: unknown) => patchCachedProfessional(old, { onlineStatus: result.onlineStatus })
        );
        if (result.onlineStatus !== next) {
          toast.warning('Stato corretto dal server', {
            description:
              result.onlineStatus === 'paused'
                ? 'Attiva almeno un canale per andare online.'
                : 'Controlla i requisiti del profilo prima di andare online.',
          });
        }
      }
    );
  }, [clearPending, confirmedStatus, onlineStatusState.isError, onlineStatusState.isLoading, queryClient, socket]);

  const status: ProfessionalOnlineStatus = confirmedStatus === 'busy'
    ? 'busy'
    : (requestedStatus ?? confirmedStatus ?? 'offline');
  const stableConfirmedStatus: ProfessionalOnlineStatus = confirmedStatus ?? 'offline';

  return {
    status,
    confirmedStatus: stableConfirmedStatus,
    isConnected,
    isLoading: onlineStatusState.isLoading,
    isError: onlineStatusState.isError,
    isUpdating: requestedStatus !== null,
    setStatus,
  };
}
